import { useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import useSWRInfinite from 'swr/infinite';

import type { PaginatedResponse, TrackContribution } from '@kangaroo/shared';

import { findBestContributionForTrack } from '../lib/circuitMapOverlay';
import {
  fetchGlobalTrackContributionsPage,
  fetchTeamTrackContributions,
  fetchTrackContributionsByIds,
  globalTrackContributionsPageKey,
  mergeContributionDetails,
  mergeTrackContributionCatalog,
  teamTrackContributionsListKey,
  trackContributionHydrateKey,
} from '../lib/trackContributions';

type UseTrackContributionCatalogOptions = {
  contributionIds?: Array<string | null | undefined>;
  trackId?: string | null;
  enabled?: boolean;
  sessionGps?: Array<{ lat: number; lon: number }> | null;
};

const circuitCatalogSwrOptions = {
  dedupingInterval: 5_000,
  focusThrottleInterval: 5_000,
} as const;

const GLOBAL_CATALOG_PAGE_SIZE = 250;
type GlobalCatalogPageKey = ReturnType<typeof globalTrackContributionsPageKey>;

function useGlobalTrackContributionCatalog(enabled: boolean) {
  const {
    data: pages,
    isLoading,
    isValidating,
    setSize,
    size,
  } = useSWRInfinite<PaginatedResponse<TrackContribution>>(
    (pageIndex, previousPage) => {
      if (!enabled) return null;
      if (previousPage && pageIndex >= previousPage.totalPages) return null;
      return globalTrackContributionsPageKey(pageIndex + 1, GLOBAL_CATALOG_PAGE_SIZE);
    },
    ([, detail, page, perPage, search]: GlobalCatalogPageKey) => fetchGlobalTrackContributionsPage({
      detail,
      page,
      perPage,
      search: search || undefined,
    }),
    {
      ...circuitCatalogSwrOptions,
      persistSize: true,
      revalidateFirstPage: false,
    },
  );
  const loadedPages = pages?.length ?? 0;
  const totalPages = pages?.[0]?.totalPages ?? 1;

  useEffect(() => {
    if (
      enabled
      && loadedPages > 0
      && loadedPages < totalPages
      && size <= loadedPages
      && !isValidating
    ) {
      void setSize(loadedPages + 1);
    }
  }, [enabled, isValidating, loadedPages, setSize, size, totalPages]);

  return {
    contributions: useMemo(
      () => pages?.flatMap((page) => page.data) ?? [],
      [pages],
    ),
    isLoading: enabled && (isLoading || loadedPages < totalPages),
  };
}

function haversineKm(
  left: { lat: number; lon: number },
  right: { lat: number; lon: number },
) {
  const radians = Math.PI / 180;
  const dLat = (right.lat - left.lat) * radians;
  const dLon = (right.lon - left.lon) * radians;
  const lat1 = left.lat * radians;
  const lat2 = right.lat * radians;
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  return 6_371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function nearbyContributionIds(
  contributions: TrackContribution[],
  gps: Array<{ lat: number; lon: number }> | null | undefined,
) {
  if (!gps?.length) return [];
  const sampled = gps.filter((_, index) => index % Math.max(1, Math.floor(gps.length / 100)) === 0);
  const center = sampled.reduce(
    (sum, point) => ({ lat: sum.lat + point.lat, lon: sum.lon + point.lon }),
    { lat: 0, lon: 0 },
  );
  center.lat /= sampled.length;
  center.lon /= sampled.length;
  return contributions
    .map((contribution) => ({
      id: contribution.id,
      distanceKm: contribution.location
        ? haversineKm(center, {
          lat: contribution.location.centerLat,
          lon: contribution.location.centerLon,
        })
        : Number.POSITIVE_INFINITY,
    }))
    .filter((candidate) => candidate.distanceKm <= 8)
    .sort((left, right) => left.distanceKm - right.distanceKm)
    .slice(0, 16)
    .map((candidate) => candidate.id);
}

export function useTrackContributionCatalog(options: UseTrackContributionCatalogOptions = {}) {
  const enabled = options.enabled ?? true;
  const [deferredSessionGps, setDeferredSessionGps] = useState<typeof options.sessionGps>(null);
  useEffect(() => {
    setDeferredSessionGps(null);
    if (!options.sessionGps?.length) return;
    const timeout = window.setTimeout(() => setDeferredSessionGps(options.sessionGps), 1_500);
    return () => window.clearTimeout(timeout);
  }, [options.sessionGps]);
  const { data: teamContributions, isLoading: teamLoading } = useSWR(
    enabled ? teamTrackContributionsListKey('draft') : null,
    () => fetchTeamTrackContributions('draft'),
    circuitCatalogSwrOptions,
  );
  // The global summaries are the searchable circuit index. Load them even when
  // a session has no GPS/track match; detailed geometry is still hydrated only
  // for the selected, matched, or nearby circuits below.
  const shouldLoadGlobal = enabled;
  const {
    contributions: globalContributions,
    isLoading: globalLoading,
  } = useGlobalTrackContributionCatalog(shouldLoadGlobal);

  const summaryContributions = useMemo(
    () => mergeTrackContributionCatalog(teamContributions, globalContributions),
    [globalContributions, teamContributions],
  );

  const hydrateIds = useMemo(() => {
    const ids = new Set<string>();
    for (const contributionId of options.contributionIds ?? []) {
      if (contributionId) {
        ids.add(contributionId);
      }
    }

    const bestForTrack = findBestContributionForTrack(summaryContributions, options.trackId);
    if (bestForTrack?.id) {
      ids.add(bestForTrack.id);
    }
    for (const nearbyId of nearbyContributionIds(summaryContributions, deferredSessionGps)) {
      ids.add(nearbyId);
    }

    return [...ids].sort();
  }, [deferredSessionGps, options.contributionIds, options.trackId, summaryContributions]);

  const { data: hydratedContributions = [], isLoading: hydrateLoading } = useSWR(
    enabled ? trackContributionHydrateKey(hydrateIds) : null,
    () => fetchTrackContributionsByIds(hydrateIds),
  );

  const contributions = useMemo(
    () => mergeContributionDetails(summaryContributions, ...hydratedContributions),
    [hydratedContributions, summaryContributions],
  );

  return {
    contributions,
    summaryContributions,
    isLoading: enabled && (teamLoading || (shouldLoadGlobal && globalLoading)),
    isHydrating: enabled && hydrateLoading,
  };
}

export function useTrackContributionCatalogSummary(enabled = true) {
  const { data: teamContributions, isLoading: teamLoading } = useSWR(
    enabled ? teamTrackContributionsListKey('draft') : null,
    () => fetchTeamTrackContributions('draft'),
    circuitCatalogSwrOptions,
  );
  const {
    contributions: globalContributions,
    isLoading: globalLoading,
  } = useGlobalTrackContributionCatalog(enabled);

  const contributions = useMemo(
    () => mergeTrackContributionCatalog(teamContributions, globalContributions),
    [globalContributions, teamContributions],
  );

  return {
    contributions,
    isLoading: enabled && (teamLoading || globalLoading),
  };
}
